import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { useTheme } from '../../contexts/ThemeContext';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import UniversitySearch from '../../components/UniversitySearch/UniversitySearch';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { SUPPORTED_UNIVERSITY_IDS } from '../../config/supportedUniversities';
import { trackSignupConversion } from '../../utils/googleAdsConversion';
import './SignUp.css';

const SignUp: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register, registerWithGoogle, user, showGoogleSetupModal } = useAuth();
    const { isDarkMode } = useTheme();

    // Get pre-fill data from URL params (from claim listing flow)
    const listingId = searchParams.get('listingId');
    const prefillFirstName = searchParams.get('firstName') || '';
    const prefillLastName = searchParams.get('lastName') || '';
    const prefillUniversityId = searchParams.get('universityId') || '';
    const redirectPath = searchParams.get('redirect') || '/profile';

    const [formData, setFormData] = useState({
        firstName: prefillFirstName,
        lastName: prefillLastName,
        email: '',
        instagram: '',
        universityId: prefillUniversityId,
        universityName: '',
        password: '',
        confirmPassword: ''
    });

    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const googleButtonRef = useRef<HTMLDivElement>(null);

    const [isDarkModeState, setIsDarkModeState] = useState(false);

    useEffect(() => {
        setIsDarkModeState(isDarkMode);
    }, [isDarkMode]);

    const { initializeGoogleAuth, renderGoogleButton } = useGoogleAuth({
        onSuccess: async (credential: string) => {
            try {
                setIsLoading(true);
                const result = await registerWithGoogle(credential);
                if (result.requiresSetup && result.setupData) {
                    // Store listing claim info if coming from claim flow
                    if (listingId) {
                        localStorage.setItem('pending_listing_claim', listingId);
                    }
                    showGoogleSetupModal(result.setupData);
                } else {
                    // Account created/authenticated successfully via Google
                    trackSignupConversion();
                    // If no setup required, claim listing if needed
                    const pendingListingId = listingId || localStorage.getItem('pending_listing_claim');
                    if (pendingListingId) {
                        await claimListingAfterAuth(pendingListingId);
                    } else {
                        navigate(redirectPath);
                    }
                }
            } catch (error) {
                setError(error instanceof Error ? error.message : 'Google registration failed');
            } finally {
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            setError(error.message || 'Google authentication failed');
        },
    });


    const claimListingAfterAuth = async (listingIdToClaim: string) => {
        try {
            const authToken = localStorage.getItem('auth_token');
            if (authToken) {
                const response = await fetch(`${API_BASE_URL}/api/v1/subleases/${listingIdToClaim}/claim`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    localStorage.removeItem('pending_listing_claim');
                    navigate(`/listing/${listingIdToClaim}`);
                } else {
                    navigate(redirectPath);
                }
            } else {
                navigate(redirectPath);
            }
        } catch (error) {
            console.error('Error claiming listing:', error);
            navigate(redirectPath);
        }
    };

    // Redirect if already logged in
    useEffect(() => {
        if (user) {
            navigate('/profile');
        }
    }, [user, navigate]);

    // Pre-fill form when URL params are present (from claim listing flow)
    useEffect(() => {
        if (prefillFirstName || prefillLastName || prefillUniversityId) {
            setFormData(prev => ({
                ...prev,
                firstName: prefillFirstName || prev.firstName,
                lastName: prefillLastName || prev.lastName,
                universityId: prefillUniversityId || prev.universityId,
            }));
        }
    }, [prefillFirstName, prefillLastName, prefillUniversityId]);


    // Initialize Google Auth
    useEffect(() => {
        initializeGoogleAuth();
        // Small delay to ensure the element is rendered
        setTimeout(() => {
            if (googleButtonRef.current) {
                renderGoogleButton(googleButtonRef.current, 'outline', isDarkMode);
            }
        }, 100);
    }, [initializeGoogleAuth, renderGoogleButton, isDarkMode]);

    // Re-render Google button when dark mode changes
    useEffect(() => {
        if (googleButtonRef.current && window.google) {
            renderGoogleButton(googleButtonRef.current, 'outline', isDarkMode);
        }
    }, [isDarkMode, renderGoogleButton]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        let stateKey = name;
        // Map obfuscated field names back to state keys
        if (name === 'signup_user_email') stateKey = 'email';
        if (name === 'signup_user_pwd') stateKey = 'password';
        if (name === 'signup_user_confirm_pwd') stateKey = 'confirmPassword';

        setFormData(prev => ({
            ...prev,
            [stateKey]: value
        }));
        setError(''); // Clear error when user starts typing
    };

    const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
        e.preventDefault();
        setError('');

        // Validate all required fields are filled
        if (!formData.firstName || !formData.firstName.trim()) {
            setError('First name is required');
            return;
        }

        if (!formData.lastName || !formData.lastName.trim()) {
            setError('Last name is required');
            return;
        }

        if (!formData.email || !formData.email.trim()) {
            setError('Email is required');
            return;
        }

        if (!formData.password || !formData.password.trim()) {
            setError('Password is required');
            return;
        }

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        try {
            setIsLoading(true);
            const result = await register(
                formData.email,
                formData.password,
                formData.firstName,
                formData.lastName,
                undefined,
                formData.universityId || undefined,
                formData.instagram.trim() || undefined,
                formData.universityName || undefined
            );

            if (result.success) {
                // New account created via email/password
                trackSignupConversion();
                // If this is a claim listing flow, associate the listing with the user
                if (listingId) {
                    await claimListingAfterAuth(listingId);
                } else {
                    // Navigate to redirect path or profile
                    navigate(redirectPath);
                }
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to determine input type - always use "text" to avoid Safari password prompts
    // We use CSS -webkit-text-security: disc to mask the password visually
    const getPasswordInputType = () => {
        return "text";
    };

    const getConfirmPasswordInputType = () => {
        return "text";
    };

    return (
        <div className={`signup-page ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className="signup-container">
                <div className="signup-form-container">
                    <div className="signup-header">
                        <h1>Sign Up</h1>
                        <p>Message others and create your own listings!</p>
                    </div>

                    <div className="signup-content">
                        {error && (
                            <div className="signup-error-message">
                                {error}
                            </div>
                        )}

                        {/* Using div instead of form to prevent Safari password manager from triggering on every keystroke */}
                        <div className="signup-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        id="signup-firstName"
                                        placeholder="First Name"
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        id="signup-lastName"
                                        placeholder="Last Name"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    name="signup_user_email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    id="signup-user-email"
                                    placeholder="Email"
                                    autoComplete="off"
                                    data-lpignore="true"
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    name="instagram"
                                    value={formData.instagram}
                                    onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value.replace(/^@/, '') }))}
                                    id="signup-instagram"
                                    placeholder="Instagram (optional)"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="form-group">
                                <UniversitySearch
                                    value={formData.universityId}
                                    onChange={(value) => setFormData(prev => ({ ...prev, universityId: value }))}
                                    onInputChange={(name) => setFormData(prev => ({ ...prev, universityName: name }))}
                                    placeholder="University or City (optional)"
                                    className="signup-page-university-search"
                                    supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                                    disableCapsule={true}
                                    showCities={true}
                                />
                            </div>

                            <div className="form-group password-input-wrapper">
                                {/* Visual placeholder overlay - Safari can't detect this */}
                                {formData.password.length === 0 && (
                                    <span className="visual-placeholder">Password (6-50 characters)</span>
                                )}
                                <input
                                    type="text"
                                    name="signup_secret_1"
                                    value={showPassword ? formData.password : '•'.repeat(formData.password.length)}
                                    onChange={(e) => {
                                        if (showPassword) {
                                            setFormData(prev => ({ ...prev, password: e.target.value }));
                                        } else {
                                            const newLen = e.target.value.length;
                                            const oldLen = formData.password.length;
                                            if (newLen < oldLen) {
                                                setFormData(prev => ({ ...prev, password: prev.password.slice(0, newLen) }));
                                            } else if (newLen > oldLen) {
                                                const newChars = e.target.value.slice(oldLen).replace(/•/g, '');
                                                setFormData(prev => ({ ...prev, password: prev.password + newChars }));
                                            }
                                        }
                                    }}
                                    minLength={6}
                                    maxLength={50}
                                    autoComplete="off"
                                    data-lpignore="true"
                                    data-form-type="other"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide" : "Show"}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <div className="form-group password-input-wrapper">
                                {/* Visual placeholder overlay - Safari can't detect this */}
                                {formData.confirmPassword.length === 0 && (
                                    <span className="visual-placeholder">Confirm Password</span>
                                )}
                                <input
                                    type="text"
                                    name="signup_secret_2"
                                    value={showConfirmPassword ? formData.confirmPassword : '•'.repeat(formData.confirmPassword.length)}
                                    onChange={(e) => {
                                        if (showConfirmPassword) {
                                            setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
                                        } else {
                                            const newLen = e.target.value.length;
                                            const oldLen = formData.confirmPassword.length;
                                            if (newLen < oldLen) {
                                                setFormData(prev => ({ ...prev, confirmPassword: prev.confirmPassword.slice(0, newLen) }));
                                            } else if (newLen > oldLen) {
                                                const newChars = e.target.value.slice(oldLen).replace(/•/g, '');
                                                setFormData(prev => ({ ...prev, confirmPassword: prev.confirmPassword + newChars }));
                                            }
                                        }
                                    }}
                                    minLength={6}
                                    maxLength={50}
                                    autoComplete="off"
                                    data-lpignore="true"
                                    data-form-type="other"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    aria-label={showConfirmPassword ? "Hide" : "Show"}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>

                            <button
                                type="button"
                                className="signup-submit-btn"
                                disabled={isLoading}
                                onClick={handleSubmit}
                            >
                                {isLoading ? 'Signing Up...' : 'Sign Up'}
                            </button>
                        </div>

                        <div className="signup-divider">
                            <span>or</span>
                        </div>

                        <div className="google-auth-container">
                            <div className="google-auth-btn-container google-auth-btn-container--dmmono">
                                <div ref={googleButtonRef} className="google-auth-btn-google" />
                                <div className="google-auth-btn-overlay" aria-hidden="true">
                                    <span className="google-auth-btn-overlay-text">Sign up with Google</span>
                                </div>
                            </div>
                            {!window.google && (
                                <div className="google-fallback-btn">
                                    <p>Loading Google Sign-In...</p>
                                </div>
                            )}
                        </div>


                        <div className="signup-footer">
                            <p>
                                Already have an account?{' '}
                                <Link to="/" className="signin-link">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default SignUp;
